import {
  CampaignForm,
  type CampaignStep,
} from "Components/Admin/Campaigns/CampaignForm/CampaignForm";
import { STEP_ORDER } from "Components/Admin/Campaigns/CampaignForm/useCampaignStepper";
import { useUpdateCampaignMutation } from "Api/Mutations/Admin/useUpdateCampaignMutation";
import { toast } from "sonner";
import type { CampaignFormData } from "Components/Admin/Campaigns/CampaignForm/campaignFormSchema";
import {
  CampaignLifecycleStatus,
  type AdminCampaignDto,
  type ApiProblemDetails,
} from "Infrastructure/Api/Api";

import { Resources, useResource } from "Translations/Resources";
import { useSearchParams } from "Navigation/useSearchParams";

const RESOURCES = Resources.Admin.Campaigns.Messages;

type Props = {
  campaign: AdminCampaignDto;
};

export const CampaignEditContent = ({ campaign }: Props) => {
  const { t } = useResource();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeStepParam = searchParams.get("activeStep");
  const activeStep: CampaignStep =
    activeStepParam && STEP_ORDER.includes(activeStepParam as CampaignStep)
      ? (activeStepParam as CampaignStep)
      : "basic-info";

  const handleStepChange = (step: CampaignStep) => {
    setSearchParams({ activeStep: step });
  };

  const { mutateAsync: updateCampaign, isPending } = useUpdateCampaignMutation({
    onError: (error: Error) => {
      if (typeof error === "object" && error !== null && "data" in error) {
        const errorData = error.data as Record<string, unknown>;
        if (
          errorData &&
          typeof errorData === "object" &&
          "errors" in errorData
        ) {
          const errors = (errorData as ApiProblemDetails).errors || {};
          if (errors.Code) {
            return toast.error(t(RESOURCES.CodeExistsError));
          }
        }
      }
      return toast.error(t(RESOURCES.UpdateError));
    },
  });

  const { campaignID } = campaign;

  const toUpdateRequest = (c: AdminCampaignDto) => ({
    name: c.name,
    code: c.code,
    description: c.description,
    header: c.header,
    linkName: c.linkName,
    linkUrl: c.linkUrl,
    successfulPaymentText: c.successfulPaymentText,
    color: c.color,
    paymentInstructionsText: c.paymentInstructionsText,
    onlyWithMooneText: c.onlyWithMooneText,
    receiverUserID: c.receiverUserID,
    receiverIdentityUserID: c.receiverIdentityUserID,
    isAffiliateEnabled: c.isAffiliateEnabled,
    startDate: c.startDate,
    endDate: c.endDate,
  });

  const formDataToRequest = (data: CampaignFormData) => ({
    ...data,
    description: data.description || "",
    header: data.header || null,
    linkName: data.linkName || null,
    linkUrl: data.linkUrl || null,
    successfulPaymentText: data.successfulPaymentText || null,
    receiverUserID: data.receiverUserID || null,
    receiverIdentityUserID: data.receiverIdentityUserID ?? null,
    startDate: data.dateMode === "unlimited" ? null : data.startDate || null,
    endDate: data.dateMode === "unlimited" ? null : data.endDate || null,
    status: campaign.status,
  });

  const handleSubmit = async (data: CampaignFormData) => {
    if (!campaignID) {
      return;
    }

    await updateCampaign(
      { id: campaignID, data: formDataToRequest(data) },
      { onSuccess: () => toast.success(t(RESOURCES.UpdateSuccess)) },
    );
  };

  const handlePublish = async (data: CampaignFormData) => {
    if (!campaignID) {
      return;
    }

    await updateCampaign(
      {
        id: campaignID,
        data: {
          ...formDataToRequest(data),
          status: CampaignLifecycleStatus.Published,
        },
      },
      { onSuccess: () => toast.success(t(RESOURCES.PublishSuccess)) },
    );
  };

  const updateStatus = (
    status: CampaignLifecycleStatus,
    successMessage: string,
  ) => {
    if (!campaignID) {
      return;
    }

    updateCampaign(
      { id: campaignID, data: { ...toUpdateRequest(campaign), status } },
      { onSuccess: () => toast.success(successMessage) },
    );
  };

  const handleArchive = () =>
    updateStatus(CampaignLifecycleStatus.Archived, t(RESOURCES.ArchiveSuccess));
  const handleUnpublish = () =>
    updateStatus(CampaignLifecycleStatus.Draft, t(RESOURCES.UnpublishSuccess));

  return (
    <CampaignForm
      isEditMode
      defaultValues={{
        ...campaign,
        // Campaigns saved before the twin: preselect the grant the receiver resolves to
        receiverIdentityUserID:
          campaign.receiverIdentityUserID ??
          campaign.receiverMerchantUser?.identityUserID,
        header: campaign.header || undefined,
        linkName: campaign.linkName || undefined,
        linkUrl: campaign.linkUrl || undefined,
        successfulPaymentText: campaign.successfulPaymentText || undefined,
        startDate: campaign.startDate || undefined,
        endDate: campaign.endDate || undefined,
      }}
      onSubmit={handleSubmit}
      onPublish={handlePublish}
      onArchive={handleArchive}
      onUnpublish={handleUnpublish}
      isSubmitting={isPending}
      campaign={campaign}
      activeStep={activeStep}
      onStepChange={handleStepChange}
    />
  );
};
